import { useState } from "react";
import { useParams, useNavigate, generatePath } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box, Typography, Card, CardContent, Chip, Button, TextField,
  CircularProgress, Alert, Snackbar, Stack, Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getAdminReports, resolveReport } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";

const statusColor = { PENDING: "warning", REVIEWED: "info", DISMISSED: "default", ACTIONED: "success" };

const ReportDetail = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [adminNotes, setAdminNotes] = useState("");
  const [snackbar, setSnackbar] = useState(null);

  // Fetch single report via list endpoint with implicit match — or we rely on reportId
  // The API doesn't expose GET /devAdmin/reports/:reportId, so we use the list with a high limit
  // and filter client-side. For MVP this is acceptable; north star is a dedicated endpoint.
  const { data, isLoading } = useQuery({
    queryKey: ["admin-report-detail", reportId],
    queryFn: async () => {
      const res = await getAdminReports({ page: 1, limit: 100 });
      return res.reports?.find((r) => r._id === reportId) || null;
    },
  });

  const mutation = useMutation({
    mutationFn: ({ status }) => resolveReport(reportId, { status, adminNotes }),
    onSuccess: (_, { status }) => {
      qc.invalidateQueries({ queryKey: ["admin-reports"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setSnackbar({ severity: "success", message: `Report marked ${status}.` });
      setTimeout(() => navigate(frontendRoutes.ADMIN_REPORTS), 1500);
    },
    onError: (err) => {
      setSnackbar({ severity: "error", message: err?.data?.message || "Failed to update report." });
    },
  });

  if (isLoading) return <CircularProgress />;
  if (!data) return <Alert severity="error">Report not found.</Alert>;

  const report = data;
  const isActioned = ["DISMISSED", "ACTIONED"].includes(report.status);

  return (
    <Box maxWidth={600}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(frontendRoutes.ADMIN_REPORTS)} sx={{ mb: 2 }}>
        Back to Reports
      </Button>

      <Typography variant="h5" fontWeight={700} mb={3}>Report Detail</Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Chip label={report.entityType} />
            <Chip label={report.status} color={statusColor[report.status]} />
          </Stack>
          <Typography variant="body2"><strong>Reason:</strong> {report.reason?.replace(/_/g, " ")}</Typography>
          {report.description && (
            <Typography variant="body2" mt={1}>{report.description}</Typography>
          )}
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Reported: {new Date(report.createdAt).toLocaleString()}
          </Typography>
          {report.adminNotes && (
            <Box mt={1} p={1} bgcolor="info.light" borderRadius={1}>
              <Typography variant="caption"><strong>Admin notes:</strong> {report.adminNotes}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Quick link to entity admin page */}
      {report.entityType === "SHOP" && report.entityId && (
        <Button size="small" onClick={() => navigate(generatePath(frontendRoutes.ADMIN_SHOP_DETAIL, { shopId: report.entityId }))}>
          View Shop
        </Button>
      )}
      {report.entityType === "USER" && report.entityId && (
        <Button size="small" onClick={() => navigate(generatePath(frontendRoutes.ADMIN_USER_DETAIL, { userId: report.entityId }))}>
          View User
        </Button>
      )}

      {!isActioned && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography fontWeight={600} mb={1}>Take Action</Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Admin notes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              <strong>Dismiss</strong> — no violation found, report closed. &nbsp;
              <strong>Mark actioned</strong> — you've already taken a consequence (suspension, delist, warning). Requires notes.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate({ status: "DISMISSED" })}
              >
                Dismiss
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={mutation.isPending || !adminNotes.trim()}
                onClick={() => mutation.mutate({ status: "ACTIONED" })}
              >
                Mark actioned
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>{snackbar?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportDetail;
